import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { postAPIPayload } from "../../../apis/postApi";
import { stripePromise } from "../../helpers/constants";
import { mapPaymentApiUrl } from "../../for-service-provider/createUserApiPaths";

const paymentElementOptions = {
  fields: {
    billingDetails: {
      email: "never" as const,
      name: "never" as const,
      phone: "never" as const,
      address: "never" as const,
    },
  },
};

export type AchPaymentPanelHandle = {
  linkBankAccount: () => Promise<{ bank_account_id?: number | string } | null>;
};

type AchLinkFormInnerProps = {
  attachUrl: string;
  onError: (message: string) => void;
};

const AchLinkFormInner = forwardRef<
  AchPaymentPanelHandle,
  AchLinkFormInnerProps
>(({ attachUrl, onError }, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  useImperativeHandle(ref, () => ({
    linkBankAccount: async () => {
      if (!stripe || !elements) {
        onError("Payment system is not ready. Please try again.");
        return null;
      }

      setSubmitting(true);
      try {
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
        });

        if (error) {
          onError(error.message || "Unable to link bank account.");
          return null;
        }

        if (
          setupIntent?.status === "succeeded" &&
          setupIntent.payment_method
        ) {
          const attachResponse = await postAPIPayload({
            url: attachUrl,
            data: { payment_method_id: setupIntent.payment_method },
            token: true,
          });

          if (attachResponse?.success) {
            return attachResponse?.data || {};
          }

          onError(
            attachResponse?.message ||
              attachResponse?.data?.message ||
              "Unable to save bank account.",
          );
          return null;
        }

        onError("Bank account setup did not complete. Please try again.");
        return null;
      } catch (err: any) {
        onError(err?.message || "Unable to link bank account.");
        return null;
      } finally {
        setSubmitting(false);
      }
    },
  }));

  return (
    <div className={`space-y-3 ${submitting ? "opacity-70 pointer-events-none" : ""}`}>
      <p className="text-xs text-muted-foreground">
        Link your client&apos;s bank account securely through Stripe. ACH
        payments may take a few business days to settle.
      </p>
      <div className="stripe-ach-payment-element rounded-lg border border-border bg-card p-4">
        <PaymentElement options={paymentElementOptions} />
      </div>
    </div>
  );
});

AchLinkFormInner.displayName = "AchLinkFormInner";

type AchPaymentPanelProps = {
  userId: number | string;
  isServiceProvider?: boolean;
  onError?: (message: string) => void;
};

const AchPaymentPanel = forwardRef<AchPaymentPanelHandle, AchPaymentPanelProps>(
  ({ userId, isServiceProvider = false, onError }, ref) => {
    const innerRef = useRef<AchPaymentPanelHandle>(null);
    const [clientSecret, setClientSecret] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    useImperativeHandle(ref, () => ({
      linkBankAccount: async () => {
        if (!innerRef.current) {
          onError?.("Bank account form is not ready.");
          return null;
        }
        return innerRef.current.linkBankAccount();
      },
    }));

    useEffect(() => {
      let active = true;

      const loadSetupIntent = async () => {
        if (!userId) {
          setLoading(false);
          return;
        }

        setLoading(true);
        setLoadError("");
        try {
          const response = await postAPIPayload({
            url: mapPaymentApiUrl(
              "ach/setup-intent",
              userId,
              isServiceProvider,
            ),
            data: {},
            token: true,
          });

          if (!active) return;

          if (response?.success && response?.data?.client_secret) {
            setClientSecret(response.data.client_secret);
          } else {
            const message =
              response?.message || "Unable to start ACH setup.";
            setLoadError(message);
            onError?.(message);
          }
        } catch (err: any) {
          if (!active) return;
          const message = err?.message || "Unable to start ACH setup.";
          setLoadError(message);
          onError?.(message);
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };

      loadSetupIntent();

      return () => {
        active = false;
      };
    }, [userId, isServiceProvider, onError]);

    const elementsOptions = useMemo(
      () =>
        clientSecret
          ? {
              clientSecret,
              appearance: {
                theme: "stripe" as const,
                variables: {
                  colorBackground: "#ffffff",
                  borderRadius: "8px",
                },
              },
            }
          : undefined,
      [clientSecret],
    );

    if (loading) {
      return (
        <div className="rounded-lg border border-border bg-muted/20 p-6 text-center text-sm text-muted-foreground animate-pulse">
          Preparing secure bank connection…
        </div>
      );
    }

    if (loadError || !clientSecret) {
      return (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {loadError || "Unable to load bank account form."}
        </div>
      );
    }

    const attachUrl = mapPaymentApiUrl(
      "ach/bank-account",
      userId,
      isServiceProvider,
    );

    return (
      <Elements stripe={stripePromise} options={elementsOptions}>
        <AchLinkFormInner
          ref={innerRef}
          attachUrl={attachUrl}
          onError={(message) => onError?.(message)}
        />
      </Elements>
    );
  },
);

AchPaymentPanel.displayName = "AchPaymentPanel";

export default AchPaymentPanel;
